// Reading rules of the contatti anagrafica, kept out of the components so that
// the two things the acceptance criteria are strict about have one definition
// each: what «coperto» means (the server marker, never the length of a list) and
// what the digest configuration says (read, never promised editable).
//
// Vocabulary, deliberately: contatto / recapito / scope. Never ruolo, never
// permesso — a contatto is a recapito with a scope, not a user with a permission
// (specs/mvp2-controllo-temperature, US 17; CONTEXT.md, voce Codice operatore).

export const SCOPE_STRUTTURA = 'STRUTTURA';
export const SCOPE_REPARTO = 'REPARTO';

// The third state of the coverage marker: read, and the server did not say. It is
// not `false` — «nobody would receive the escalation» is a claim, and an absent
// marker does not support it — and it is not `true` either.
export const COPERTURA_NON_NOTA = 'non-nota';

const contattiValidi = (elenco) =>
  Array.isArray(elenco)
    ? // A recapito with no id is addressable by no route: the PUT and the DELETE
      // of both scopes are keyed on it, so a row without one can only render a
      // control that cannot work.
      elenco.filter((contatto) => contatto && contatto.id != null)
    : [];

export const normalizzaAnagraficaContatti = (risposta) => ({
  struttura: contattiValidi(risposta?.struttura),
  reparto: contattiValidi(risposta?.reparto),
  copertura: letturaCopertura(risposta),
});

/**
 * `copertoDaContattiDiStruttura` is the server's own answer to «does an
 * escalation that starts now reach anybody». Anything that is not a boolean is
 * unknown coverage, so the screen can say «not known» instead of claiming either
 * side of a question nobody answered.
 */
export function letturaCopertura(risposta) {
  const marcatore = risposta?.copertoDaContattiDiStruttura;
  return typeof marcatore === 'boolean' ? marcatore : COPERTURA_NON_NOTA;
}

/**
 * Who the person is, and where the notification actually lands. Both, because the
 * name alone does not tell a technician whether the address is the right one.
 */
export const etichettaRecapito = (contatto) => {
  const nome = [contatto?.nome, contatto?.cognome].filter(Boolean).join(' ');
  const email = contatto?.email;
  if (nome && email) return `${nome} (${email})`;
  return nome || email || '';
};

// The default reparto ("Intera struttura") holds no notification contacts: on
// that row the link decides an authorisation outcome (isCapoResponsabile), the
// anagrafica excludes it and the PUT refuses it with 400. Those people are
// already represented by the struttura step.
export const repartiConfigurabili = (reparti) =>
  (Array.isArray(reparti) ? reparti : []).filter(
    (reparto) => reparto && !reparto.repartoDefault,
  );

// The name of a group the reparti read did not name, taken from the recapito
// sitting on it. One helper for both the group standing because of that recapito
// and the one this session remembers, so the title does not change under the
// technician's hands when the last recapito goes.
const nomeDelGruppo = (contatto) =>
  contatto.scopeNome ?? `Reparto ${contatto.scopeId}`;

/**
 * The reparti a group has already been shown for, id → group, accumulated across
 * the re-reads of the anagrafica and fed from `anagrafica.reparto`.
 *
 * The memory lasts as long as the mount, and that is a limit rather than a
 * design: reload the page and an emptied reparto is gone again, because the
 * anagrafica no longer names it and no route of this contract lists the reparti
 * of an esercizio. `GET /api/reparti?manualeId.equals=` (`utils/api/reparti.js`)
 * wants a `manualeId` this screen does not have. Closing that is a separate
 * story; what this removes is the trap described below.
 */
export const ricordaRepartiDeiRecapiti = (ricordati, contattiDiReparto) => {
  const aggiornato = new Map(ricordati ?? []);
  contattiValidi(contattiDiReparto).forEach((contatto) => {
    if (contatto.scopeId == null) return;
    aggiornato.set(String(contatto.scopeId), {
      repartoId: contatto.scopeId,
      repartoNome: nomeDelGruppo(contatto),
    });
  });
  return aggiornato;
};

/**
 * One group per reparto, including the ones with no recapito: an empty reparto
 * that does not appear cannot be configured, and «no row» reads as «nothing to
 * do here» rather than as «nobody is notified for this reparto».
 *
 * A recapito whose reparto is missing from the list still gets a group of its
 * own, named after the `scopeNome` the server sent: the two reads can disagree —
 * a publication renames or drops reparti — and dropping the row would hide a
 * recipient that keeps being notified.
 *
 * `ricordati` holds the groups already shown in this session. Where the reparti
 * read gives nothing — no published gestione operativa — a group exists *only*
 * because a contatto sits on it, so removing that contatto made the group, and
 * its «Aggiungi recapito» with it, vanish for good: nothing on this screen could
 * put it back. Remembering the reparto keeps the round trip open.
 *
 * Both the fallback and the memory stop at the reparti this screen deliberately
 * hides. The default reparto is one of them: a contatto sitting on it — server
 * drift, since the anagrafica is supposed to exclude it — must not resurrect the
 * row, because the only control it would carry is an «Aggiungi recapito» whose
 * PUT answers 400 by design.
 */
export const repartiConRecapiti = (reparti, contattiDiReparto, ricordati) => {
  const contatti = contattiValidi(contattiDiReparto);
  const elenco = Array.isArray(reparti) ? reparti : [];
  // Every reparto the read returned, the non-configurable ones included: only an
  // id nobody sent is unknown enough to deserve a group of its own.
  const idLetti = new Set(
    elenco.filter(Boolean).map((reparto) => String(reparto.id)),
  );

  const gruppi = repartiConfigurabili(elenco).map((reparto) => ({
    repartoId: reparto.id,
    repartoNome: reparto.nome,
    contatti: contatti.filter(
      (contatto) => String(contatto.scopeId) === String(reparto.id),
    ),
  }));

  const notiPerId = new Set(gruppi.map((gruppo) => String(gruppo.repartoId)));
  contatti.forEach((contatto) => {
    const chiave = String(contatto.scopeId);
    if (
      contatto.scopeId == null ||
      notiPerId.has(chiave) ||
      idLetti.has(chiave)
    )
      return;
    notiPerId.add(chiave);
    gruppi.push({
      repartoId: contatto.scopeId,
      repartoNome: nomeDelGruppo(contatto),
      contatti: contatti.filter((altro) => String(altro.scopeId) === chiave),
    });
  });

  // The reparti already shown whose last recapito has just been removed. Same
  // guard as the fallback: an id the reparti read did return and that is not
  // configurable — the default reparto — must not come back through the side
  // door carrying an «Aggiungi recapito» whose PUT the server refuses with 400.
  (ricordati ? Array.from(ricordati.values()) : []).forEach((ricordato) => {
    const chiave = String(ricordato.repartoId);
    if (notiPerId.has(chiave) || idLetti.has(chiave)) return;
    notiPerId.add(chiave);
    gruppi.push({ ...ricordato, contatti: [] });
  });

  return gruppi;
};

// The mailbox is the identity of a recipient, not the row it happens to sit on:
// `uq_contatto_notifica_recapito_vivo` is unique on `lower(email)` per scope, and a
// digest that lists the same address twice reads as two people. Comparing on
// `lower(email)` here is what makes this screen agree with the table underneath.
const casella = (contatto) =>
  String(contatto.email ?? '')
    .trim()
    .toLowerCase();

// A recapito with no address cannot collide with anything and cannot be reached
// either; keyed on its own id so two of them never collapse into one entry.
const identita = (contatto) => casella(contatto) || `#${contatto.id}`;

/**
 * The recapiti this esercizio already knows, minus the ones already on the target
 * scope. Two sources: the recapiti the single read of this screen returned — the
 * same person, addressed on another scope — and the responsabili sicurezza of the
 * esercizio.
 *
 * <p>The second source is what makes an esercizio with zero recapiti configurable
 * at all. `ContattiNotificaService.recapito(contattoId)` resolves the id against
 * `responsabile_sicurezza` first and `contatto_notifica` second, so a PUT carrying
 * a responsabile's id writes a `contatto_notifica` row with their address. That is
 * a <b>copy of the address, not a grant</b>: the row written is a recapito like any
 * other, and it takes part in no authorisation check. Creating the anagrafica row
 * from here is still BSDEVPLAT-125's, and still not done here.
 *
 * <p>The responsabili come <b>after</b> the anagrafica rows: the dedup below keeps
 * the first entry for a mailbox, so a responsabile already copied into a contatto
 * collapses into that contatto — the row the removal button then addresses is the
 * one on the list. A responsabile with no address is dropped: the PUT refuses it
 * (`rifiutaSeSenzaEmail`) and a notification would have nowhere to land.
 *
 * <p>Both filters compare the <b>address</b> and not the id. On dev the same mailbox
 * exists under two different contact ids — one on the struttura rung, one on the
 * reparto one — so an id comparison offered a person already on the target scope, and
 * the write behind it either refuses the alias with a 400 or, worse, answers 200
 * carrying somebody else's row.
 */
export const contattiAssegnabiliAlloScope = (
  anagrafica,
  { scope, scopeId },
  responsabiliSicurezza,
) => {
  const noti = [
    ...contattiValidi(anagrafica?.struttura),
    ...contattiValidi(anagrafica?.reparto),
  ];

  const giaSulloScope = new Set(
    noti
      .filter((contatto) =>
        scope === SCOPE_STRUTTURA
          ? contatto.scope === SCOPE_STRUTTURA
          : contatto.scope === SCOPE_REPARTO &&
            String(contatto.scopeId) === String(scopeId),
      )
      .map(identita),
  );

  const candidati = [
    ...noti,
    ...responsabiliConIndirizzo(responsabiliSicurezza),
  ];

  const visti = new Set();
  return candidati.filter((contatto) => {
    const chiave = identita(contatto);
    if (visti.has(chiave) || giaSulloScope.has(chiave)) return false;
    visti.add(chiave);
    return true;
  });
};

/**
 * The responsabili sicurezza that can be the source of a recapito: an id to
 * address the PUT with, and an address for the notification to land on.
 */
export const responsabiliConIndirizzo = (responsabili) =>
  contattiValidi(responsabili).filter((persona) => casella(persona) !== '');

// Only the shapes the contract declares (`PT24H`) plus the neighbouring hour
// durations, so a value the server changes to something else is reported as it
// arrived instead of being silently mistranslated.
const DURATA_IN_ORE = /^PT(\d+)H$/;

const inOre = (durata) => {
  const ore = DURATA_IN_ORE.exec(durata ?? '');
  return ore ? Number(ore[1]) : null;
};

const frequenzaInParole = (durata) => {
  const ore = inOre(durata);
  if (ore === null) return durata;
  return ore === 1 ? 'ora' : `${ore} ore`;
};

const finestraInParole = (durata) => {
  const ore = inOre(durata);
  if (ore === null) return durata;
  return ore === 1 ? 'ultima ora' : `ultime ${ore} ore`;
};

// The users read times in Italian local time, so that zone goes unsaid; any
// other zone is named, because the hour would otherwise read as local.
const FUSO_ORARIO_ITALIANO = 'Europe/Rome';

/**
 * The digest configuration in one sentence, read from the single configuration
 * point of BSDEVPLAT-97. It is not editable from here and the screen must not
 * promise that it is: changing it is an environment variable and a redeploy
 * (Q8, PO validation 2026-08-06).
 */
export const descrizioneFrequenzaDigest = (digest) => {
  if (!digest?.frequenza) return null;

  const parti = [`Ogni ${frequenzaInParole(digest.frequenza)}`];
  if (digest.oraDiInvio) {
    const fuso =
      digest.fusoOrario && digest.fusoOrario !== FUSO_ORARIO_ITALIANO
        ? ` (${digest.fusoOrario})`
        : '';
    parti.push(`alle ${digest.oraDiInvio}${fuso}`);
  }

  const finestra = digest.finestraCoperta
    ? ` Copre le ${finestraInParole(digest.finestraCoperta)}.`
    : '';

  return `${parti.join(', ')}.${finestra}`;
};
